import axios from 'axios';
import type { Employee, EmployeeFilters, Classification } from '../types/Employee';

const BASE_URL = process.env.REACT_APP_API_URL;

interface PaginatedResponse<T> {
  employees: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export const employeeService = {
  getEmployees: async (page: number = 1, limit: number = 25, filters?: EmployeeFilters): Promise<PaginatedResponse<Employee>> => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString()
    });

    // Add filter parameters if they exist
    if (filters?.search && filters.search.trim()) {
      params.append('search', filters.search.trim());
    }
    if (filters?.roleId) {
      params.append('roleId', filters.roleId.toString());
    }
    if (filters?.status && filters.status.trim()) {
      params.append('status', filters.status.trim());
    }
    if (filters?.hasPortalAccess !== undefined) {
      params.append('hasPortalAccess', filters.hasPortalAccess.toString());
    }
    if (filters?.view) {
      params.append('view', filters.view);
    }

    const response = await axios.get(`${BASE_URL}/employees?${params.toString()}`);
    return response.data;
  },

  getEmployee: async (id: number): Promise<Employee> => {
    const response = await axios.get(`${BASE_URL}/employees/${id}`);
    return response.data;
  },

  createEmployee: async (employeeData: Partial<Employee>): Promise<Employee> => {
    const response = await axios.post(`${BASE_URL}/employees`, {
      ...employeeData,
      roleId: employeeData.roleId ? Number(employeeData.roleId) : undefined
    });
    return response.data;
  },

  updateEmployee: async (id: number, employeeData: Partial<Employee>): Promise<Employee> => {
    const response = await axios.put(`${BASE_URL}/employees/${id}`, {
      ...employeeData,
      roleId: employeeData.roleId ? Number(employeeData.roleId) : undefined
    });
    return response.data;
  },

  activateEmployee: async (id: number): Promise<Employee> => {
    const response = await axios.put(`${BASE_URL}/employees/${id}/activate`);
    return response.data;
  },

  deactivateEmployee: async (id: number): Promise<Employee> => {
    const response = await axios.put(`${BASE_URL}/employees/${id}/deactivate`);
    return response.data;
  },

  updatePortalAccess: async (id: number, hasPortalAccess: boolean): Promise<Employee> => {
    const response = await axios.put(`${BASE_URL}/employees/${id}/portal-access`, {
      hasPortalAccess
    });
    return response.data;
  },

  updatePortalInfo: async (id: number, portalData: { portalEmail?: string; portalRoleId?: number; foremanAreaIds?: number[]; defaultForemanAreaId?: number | null }): Promise<Employee> => {
    const response = await axios.put(`${BASE_URL}/employees/${id}/portal-info`, portalData);
    return response.data;
  },

  getEmployeeHistory: async (id: number): Promise<any[]> => {
    const response = await axios.get(`${BASE_URL}/employees/${id}/history`);
    return response.data;
  },

  getAvailableClassifications: async (id: number): Promise<Classification[]> => {
    const response = await axios.get(`${BASE_URL}/employees/${id}/classifications`);
    return response.data;
  },

  getActiveEmployeesForSelection: async (
    foremanAreaId?: number,
    serviceAreaId?: number,
    excludeTaskModalRoles: boolean = false
  ): Promise<Employee[]> => {
    const params = new URLSearchParams();
    if (foremanAreaId) {
      params.append('foremanAreaId', foremanAreaId.toString());
    }
    if (serviceAreaId) {
      params.append('serviceAreaId', serviceAreaId.toString());
    }
    if (excludeTaskModalRoles) {
      params.append('excludeTaskModalRoles', 'true');
    }
    const url = `${BASE_URL}/employees/active/selection${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await axios.get(url);
    return response.data;
  },

  checkEmployeeNumber: async (employeeNumber: string, excludeId?: number): Promise<{ isUnique: boolean; existingEmployee?: { id: number; name: string } }> => {
    const params = new URLSearchParams({ number: employeeNumber });
    if (excludeId !== undefined) {
      params.append('excludeId', excludeId.toString());
    }
    const response = await axios.get(`${BASE_URL}/employees/check-employee-number?${params.toString()}`);
    return response.data;
  }
};
